import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarClock, Smartphone, Mail, UserCircle2, Download, CreditCard,
  ArrowRight, ListVideo, QrCode, Copy, Check, Receipt, AlertTriangle, PlayCircle,
} from "lucide-react";
import api from "@/lib/api";
import Nav from "@/saas/components/Nav";
import Footer from "@/saas/components/Footer";
import SButton from "@/saas/components/SButton";
import TermsModal from "@/saas/components/TermsModal";
import PushPermissionPrompt from "@/saas/components/PushPermissionPrompt";
import { useI18n } from "@/saas/i18n";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Dashboard() {
  const { t, locale } = useI18n();
  const { user, token, refresh } = useSaasAuth();
  const [params, setParams] = useSearchParams();
  const [pollState, setPollState] = useState(null); // 'polling' | 'success' | 'failed' | null
  const [pollMsg, setPollMsg]     = useState("");
  const [payments, setPayments]   = useState([]);
  const [copiedM3u, setCopiedM3u] = useState(false);

  // Stripe redirect handler — poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !token) return;
    let cancelled = false;
    let attempts = 0;
    const MAX = 8;
    setPollState("polling");
    setPollMsg("Ödeme durumu kontrol ediliyor…");
    const tick = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/saas/checkout/status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data.status === "completed") {
          setPollState("success");
          setPollMsg("Ödeme başarılı! Aboneliğiniz aktif.");
          await refresh();
          // URL'den session_id'yi temizle
          setTimeout(() => {
            params.delete("session_id");
            setParams(params, { replace: true });
            setPollState(null);
          }, 4000);
          return;
        }
        if (res.data.status === "expired") {
          setPollState("failed");
          setPollMsg("Ödeme oturumu süresi doldu.");
          return;
        }
        if (attempts >= MAX) {
          setPollState("failed");
          setPollMsg("Ödeme durumu doğrulanamadı. Lütfen sayfayı yenileyin.");
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        if (cancelled) return;
        setPollState("failed");
        setPollMsg(err?.response?.data?.detail || "Ödeme durumu doğrulanamadı.");
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("session_id"), token]);

  // Payment history
  useEffect(() => {
    if (!token) return;
    api.get("/saas/payments", { headers: { Authorization: `Bearer ${token}` } })
       .then(r => setPayments(r.data || []))
       .catch(() => setPayments([]));
  }, [token, pollState]);

  // Phase 8.2 — Terms version gating. /saas/auth/me returns terms_status.
  // If `required` is true, customer's accepted version != current_version.
  // We render a non-dismissable TermsModal until they accept.
  const termsStatus = user?.terms_status;
  const termsGateOpen = !!(termsStatus && termsStatus.required);
  const [termsBusy, setTermsBusy] = useState(false);
  const [termsErr, setTermsErr]  = useState("");

  const acceptTermsGate = async () => {
    if (!termsStatus?.current_version || !token) return;
    setTermsErr("");
    setTermsBusy(true);
    try {
      await api.post(
        "/saas/me/accept-terms",
        { version: termsStatus.current_version },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refresh();
    } catch (e) {
      setTermsErr(e?.response?.data?.detail || "Onay kaydedilemedi.");
    } finally {
      setTermsBusy(false);
    }
  };

  const customer = user?.customer;
  const line = user?.lines?.[0];
  const isActive = customer?.status === "active";

  const { daysLeft, progress, expiry } = useMemo(() => {
    if (!line?.expiry_date) return { daysLeft: 0, progress: 0, expiry: null };
    try {
      const end = new Date(line.expiry_date).getTime();
      const start = new Date(line.created_at || customer?.created_at || Date.now() - 86400000).getTime();
      const now = Date.now();
      const total = Math.max(end - start, 1);
      const spent = Math.max(now - start, 0);
      const days = Math.max(0, Math.ceil((end - now) / 86400000));
      const prog = Math.max(0, Math.min(100, (spent / total) * 100));
      return { daysLeft: days, progress: prog, expiry: new Date(end) };
    } catch { return { daysLeft: 0, progress: 0, expiry: null }; }
  }, [line, customer]);

  const m3uUrl  = line?._id ? `${BACKEND_URL}/api/playlist/${line._id}.m3u` : null;
  const smartLink = token ? `http://178.105.60.217:3000/api/smart-link?token=${encodeURIComponent(token)}` : null;
  const qrSrc   = smartLink ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&bgcolor=0d0d0d&color=f5a623&data=${encodeURIComponent(smartLink)}` : null;

  const copyM3u = async () => {
    if (!m3uUrl) return;
    try {
      await navigator.clipboard.writeText(m3uUrl);
      setCopiedM3u(true);
      setTimeout(() => setCopiedM3u(false), 1800);
    } catch {
      window.prompt("Linki kopyalayın:", m3uUrl);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      <Nav />
      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {/* Stripe success/fail banner */}
          {pollState && (
            <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
              pollState === "success" ? "bg-ok/10 border-ok/40 text-ok"
              : pollState === "failed" ? "bg-err/10 border-err/40 text-err"
              : "bg-gold/10 border-gold/30 text-gold"
            }`} data-testid="dash-payment-banner">
              {pollState === "success" ? <Check size={18} /> :
               pollState === "failed"  ? <AlertTriangle size={18} /> :
                                         <CalendarClock size={18} className="animate-spin" />}
              <span>{pollMsg}</span>
            </div>
          )}

          <div className="mb-10">
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="dash-greeting">
              {t("dash.hello", { name: customer?.full_name?.split(" ")[0] || customer?.email?.split("@")[0] || "—" })}
            </h1>
            <p className="text-white/60 mt-2">{t("dash.welcome")}</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {/* Subscription */}
            <div className="lg:col-span-2 relative p-8 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 via-transparent to-transparent overflow-hidden" data-testid="dash-subscription">
              <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-gold/20 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarClock className="w-4 h-4 text-gold" />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-gold">Abonelik</span>
                </div>

                {isActive ? (
                  <>
                    <div className="flex items-end gap-5 mb-6 flex-wrap">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Paket</div>
                        <div className="font-display text-3xl font-extrabold" data-testid="dash-package-name">{line.package_name || "Subscription"}</div>
                      </div>
                      <div className="px-3 py-1 rounded-full bg-ok/15 border border-ok/40 inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-ok">Aktif</span>
                      </div>
                    </div>
                    <div className="space-y-4 mb-8">
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-white/60">Bitiş tarihi</span>
                        <span className="font-semibold" data-testid="dash-expiry">{fmtDate(expiry)}</span>
                      </div>
                      <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-gold to-gold-hover rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-white/60" data-testid="dash-days-left"><strong className="text-gold font-mono">{daysLeft}</strong> gün kaldı</span>
                      </div>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      <SButton to="/watch" icon={PlayCircle} data-testid="dash-open-player">Web Player'da İzle</SButton>
                      <SButton to="/plans" variant="outline" icon={CreditCard}>Yenile</SButton>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-3 py-1 mb-4 rounded-full bg-err/15 border border-err/40 inline-flex items-center gap-1.5 w-fit">
                      <span className="w-1.5 h-1.5 rounded-full bg-err" />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-err">Aboneliğiniz Yok</span>
                    </div>
                    <p className="text-white/70 mb-6">Hizmeti kullanabilmek için bir paket satın alın.</p>
                    <SButton to="/plans" size="lg" iconRight={ArrowRight} data-testid="dash-choose-plan">
                      Paketleri Görüntüle
                    </SButton>
                  </>
                )}
              </div>
            </div>

            {/* QR + M3U */}
            <div className="p-6 rounded-2xl border border-white/10 bg-surface/60 flex flex-col items-center text-center" data-testid="dash-m3u-card">
              <div className="flex items-center gap-2 mb-4 self-start">
                <QrCode className="w-4 h-4 text-gold" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-gold">Telefonuna Aktar</span>
              </div>
              {isActive && qrSrc ? (
                <>
                  <div className="bg-white/5 border border-gold/30 rounded-xl p-2 mb-3">
                    <img src={qrSrc} alt="M3U QR" className="w-44 h-44 rounded" data-testid="dash-qr-img" />
                  </div>
                  <p className="text-[10px] text-white/40 font-mono mb-3">QR'ı IPTV uygulamanızla okutun</p>
                  <button
                    onClick={copyM3u}
                    style={{ display: "none" }}
                    className={`w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition flex items-center justify-center gap-2 ${
                      copiedM3u ? "bg-ok/15 text-ok border border-ok/40" : "bg-gold text-black hover:bg-gold-hover"
                    }`}
                    data-testid="dash-copy-m3u"
                  >
                    {copiedM3u ? <><Check size={14} /> Kopyalandı</> : <><ListVideo size={14} /> M3U Linkini Kopyala</>}
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 py-12 text-white/40 text-sm">
                  <QrCode size={42} className="mb-3 opacity-30" />
                  <p>Aboneliğiniz aktif olduğunda M3U linki ve QR kodu burada görünecek.</p>
                </div>
              )}
            </div>

            {/* Payment History */}
            <div className="lg:col-span-2 p-8 rounded-2xl border border-white/10 bg-surface/60" data-testid="dash-payments">
              <div className="flex items-center gap-2 mb-5">
                <Receipt className="w-4 h-4 text-gold" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-gold">Ödeme Geçmişi</span>
              </div>
              {payments.length === 0 ? (
                <p className="text-white/40 text-sm italic py-6 text-center">Henüz ödeme yok.</p>
              ) : (
                <div className="overflow-hidden rounded-xl border border-white/5">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-black/40 text-[10px] font-mono uppercase tracking-widest text-white/50">
                      <tr>
                        <th className="p-3">Tarih</th>
                        <th className="p-3">Paket</th>
                        <th className="p-3 text-right">Tutar</th>
                        <th className="p-3 text-right">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {payments.map(p => (
                        <tr key={p.session_id} className="hover:bg-white/5" data-testid={`payment-row-${p.session_id}`}>
                          <td className="p-3 text-white/70 font-mono text-xs">{fmtDate(p.created_at)}</td>
                          <td className="p-3 font-semibold">{p.plan_name || "—"}</td>
                          <td className="p-3 text-right font-mono">{p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</td>
                          <td className="p-3 text-right">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                              p.status === "completed" ? "bg-ok/15 text-ok"
                              : p.status === "pending"   ? "bg-gold/15 text-gold"
                              :                            "bg-white/10 text-white/50"
                            }`}>
                              {p.status === "completed" ? "Başarılı"
                               : p.status === "pending" ? "Bekliyor"
                               : p.status === "expired" ? "Süresi Doldu"
                               : p.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Account */}
            <div className="p-8 rounded-2xl border border-white/10 bg-surface/60">
              <div className="flex items-center gap-2 mb-4">
                <UserCircle2 className="w-4 h-4 text-gold" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-gold">Hesap</span>
              </div>
              <div className="space-y-4 text-sm">
                <KV icon={Mail}        label="E-posta" v={customer?.email} />
                <KV icon={UserCircle2} label="Ad Soyad" v={customer?.full_name} />
                <KV icon={Smartphone}  label="IPTV Kullanıcı Adı" v={line?.username} mono />
                <KV icon={Download}    label="IPTV Şifre" v={line?.password} mono />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {/* Phase 8.2 — Terms version gating: kullanıcı onaylamadan dashboard'a erişemez */}
      {termsGateOpen && (
        <TermsModal
          open={true}
          forced={true}
          version={termsStatus.current_version}
          showAccept={!termsBusy}
          onAccept={acceptTermsGate}
        />
      )}
      {termsErr && termsGateOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[101] px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-mono" data-testid="terms-gate-err">
          {termsErr}
        </div>
      )}

      {/* Phase 10 — Push permission prompt (only shown once when permission default) */}
      {!termsGateOpen && <PushPermissionPrompt />}
    </div>
  );
}

function KV({ icon: Icon, label, v, mono }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-0.5">{label}</div>
        <div className={`text-sm truncate ${mono ? "font-mono text-gold" : ""}`}>{v || "—"}</div>
      </div>
    </div>
  );
}
